import React from "react";
import { Box, Typography, Modal, Button } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import type { DeleteSalesOrderModalProps } from "../interfaces/modals";
import { deleteModalStyle } from "../constants/modalConstants";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

import {
  LOG_LEVEL,
  buildLogContext,
  sendAppLog
} from "../../../Utils/appLogger";

const DeleteSalesOrderModal: React.FC<DeleteSalesOrderModalProps> = ({
  open,
  onClose,
  salesOrder
}) => {
  const { t } = useKivunovaTranslation();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const handleDelete = () => {
    sendAppLog(
      LOG_LEVEL.INFO,
      `Deleting sales order: ${salesOrder.orderId}`,
      buildLogContext(
        "sales-order",
        "delete-attempt",
        userDetails?.id,
        userDetails?.companyId,
        userDetails?.storeId,
        { salesOrderId: salesOrder.orderId }
      )
    );
    // TODO: implement API call when delete mutation is available
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={deleteModalStyle} color="black">
        <WarningAmberRoundedIcon color="warning" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" mb={1} fontSize={18}>
          {t(
            "delete-sales-order-confirm",
            "Do you really want to delete this sales order?"
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t("order", "Order")}: {salesOrder.orderNumber}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {t("action-cannot-be-undone", "This action cannot be undone.")}
        </Typography>

        <Box display="flex" justifyContent="center" gap={2}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              backgroundColor: "white",
              color: BRAND_COLOR,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                opacity: 0.8,
                color: "white"
              },
              textTransform: "none"
            }}
          >
            {t("no", "No")}
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            {t("yes", "Yes")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default DeleteSalesOrderModal;
